/**
 * LowBalanceMonitor — web port. Monitors voice-time balance and surfaces
 * escalating toasts (info → warning → error) as the user approaches /
 * exhausts their entitlement. Renders nothing.
 */

import { useEffect, useRef } from 'react';
import { useEntitlements } from '../context/EntitlementsContext';
import { useToast } from '../context/ToastContext';
import { formatVoiceTime } from '../api/billingApi';
import logger from '../utils/logger';

export default function LowBalanceMonitor() {
  const {
    billingEnabled,
    effectiveBalance,
    isLowBalance,
    isWarningBalance,
    isCriticalBalance,
    hasUnmeteredAccess,
  } = useEntitlements();
  const { showToast } = useToast();

  const hasShownLow = useRef(false);
  const hasShownWarning = useRef(false);
  const hasShownCritical = useRef(false);

  useEffect(() => {
    if (!billingEnabled || hasUnmeteredAccess || effectiveBalance === null) {
      return;
    }

    if (isCriticalBalance && !hasShownCritical.current) {
      logger.warn('[LowBalanceMonitor] CRITICAL balance detected', {
        effectiveBalance,
      });
      showToast('Your voice time has run out', { variant: 'error' });
      hasShownCritical.current = true;
      return;
    }

    if (isWarningBalance && !hasShownWarning.current && !isCriticalBalance) {
      logger.warn('[LowBalanceMonitor] Warning balance detected', {
        effectiveBalance,
      });
      showToast('Less than 1 minute of voice time remaining', {
        variant: 'warning',
      });
      hasShownWarning.current = true;
      return;
    }

    if (
      isLowBalance &&
      !hasShownLow.current &&
      !isWarningBalance &&
      !isCriticalBalance
    ) {
      showToast(
        `${formatVoiceTime(effectiveBalance)} of voice time remaining`,
        { variant: 'info' },
      );
      hasShownLow.current = true;
    }

    if (effectiveBalance > 120) {
      hasShownLow.current = false;
      hasShownWarning.current = false;
      hasShownCritical.current = false;
    }
  }, [
    billingEnabled,
    effectiveBalance,
    isLowBalance,
    isWarningBalance,
    isCriticalBalance,
    hasUnmeteredAccess,
    showToast,
  ]);

  return null;
}
